import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ArrowsFullscreen, XLg } from 'react-bootstrap-icons';
import CarImage from '../../common/ui/CarImage';
import language from '../../data/language';
import { currentLanguage } from '../../common/ui/notify';

/**
 * Main photo + thumbnail strip + full-screen viewer.
 * Works with zero images (placeholder), one image (no arrows) and many.
 */
const CarGallery = ({images = [], alt = "", cornerBadge = null}) => {
    const lang = currentLanguage();
    const strings = language[lang].details;

    const [index, setIndex] = useState(0);
    const [lightbox, setLightbox] = useState(false);

    const count = images.length;

    // A shorter gallery (after a photo was deleted) must not leave the pointer
    // past the end of the array.
    useEffect(() => {
        if (index > count - 1) setIndex(count > 0 ? count - 1 : 0);
    }, [count, index]);

    const step = useCallback((direction) => {
        if (count < 2) return;
        setIndex((prev) => (prev + direction + count) % count);
    }, [count]);

    // Arrow keys and Escape while the full-screen viewer is open.
    useEffect(() => {
        if (!lightbox) return undefined;

        const onKeyDown = (event) => {
            if (event.key === 'Escape') setLightbox(false);
            if (event.key === 'ArrowLeft') step(-1);
            if (event.key === 'ArrowRight') step(1);
        };

        document.addEventListener('keydown', onKeyDown);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [lightbox, step]);

    const current = count > 0 ? images[Math.min(index, count - 1)] : null;

    return (
        <div className="car-gallery">
            <div className={`car-gallery-stage ${cornerBadge ? "has-corner-badge" : ""}`}>
                <CarImage src={current} alt={alt} className="car-gallery-main" loading="eager" />

                {cornerBadge && (
                    <img src={cornerBadge} alt="Auction" className="car-gallery-sticker" aria-hidden="true" />
                )}

                {count > 0 && (
                    <button
                        type="button"
                        className="car-gallery-expand"
                        aria-label={strings.open_gallery}
                        onClick={() => setLightbox(true)}
                    >
                        <ArrowsFullscreen aria-hidden="true" />
                    </button>
                )}

                {count > 1 && (
                    <>
                        <button type="button" className="car-gallery-nav car-gallery-nav-prev"
                                aria-label={strings.prev_photo} onClick={() => step(-1)}>
                            <ChevronLeft aria-hidden="true" />
                        </button>
                        <button type="button" className="car-gallery-nav car-gallery-nav-next"
                                aria-label={strings.next_photo} onClick={() => step(1)}>
                            <ChevronRight aria-hidden="true" />
                        </button>
                        <span className="car-gallery-counter">{index + 1} / {count}</span>
                    </>
                )}

                {count === 0 && <span className="car-gallery-empty">{strings.no_images}</span>}
            </div>

            {count > 1 && (
                <div className="car-gallery-thumbs" role="tablist" aria-label={strings.photo_of}>
                    {images.map((image, position) => (
                        <button
                            key={`${image}-${position}`}
                            type="button"
                            role="tab"
                            aria-selected={position === index}
                            aria-label={`${strings.photo_of} ${position + 1}`}
                            className={`car-gallery-thumb ${position === index ? "is-active" : ""}`}
                            onClick={() => setIndex(position)}
                        >
                            <CarImage src={image} alt="" />
                        </button>
                    ))}
                </div>
            )}

            {lightbox && current && (
                <div className="car-lightbox" role="dialog" aria-modal="true" aria-label={strings.open_gallery}>
                    <div className="car-lightbox-backdrop" onClick={() => setLightbox(false)}></div>

                    <button type="button" className="car-lightbox-close" aria-label={strings.close_gallery}
                            onClick={() => setLightbox(false)}>
                        <XLg aria-hidden="true" />
                    </button>

                    <figure className="car-lightbox-figure">
                        <img src={current} alt={alt} />
                    </figure>

                    {count > 1 && (
                        <>
                            <button type="button" className="car-lightbox-nav car-lightbox-nav-prev"
                                    aria-label={strings.prev_photo} onClick={() => step(-1)}>
                                <ChevronLeft aria-hidden="true" />
                            </button>
                            <button type="button" className="car-lightbox-nav car-lightbox-nav-next"
                                    aria-label={strings.next_photo} onClick={() => step(1)}>
                                <ChevronRight aria-hidden="true" />
                            </button>
                            <span className="car-lightbox-counter">{index + 1} / {count}</span>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default CarGallery;
